import React, { useEffect, useMemo, useState } from 'react';
import { saveAs } from 'file-saver';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import { useProjectScenarios } from '../../../context/ProjectScenariosContext';
import ScenarioSwitcher from './ScenarioSwitcher';
import { ProjectsScenariosRestDataProvider } from '../../../data/ProjectScenariosDataProvider';
import { Project } from '../../../data/ProjectsRestDataProvider';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import { ExcelExportRestDataProvider } from '../../../data/ExcelExportRestDataProvider';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import './ProjectPricing.scss';

interface ProjectPricingProps {
  scenarioId: string;
  setScenarioId: (id: string) => void;
  project?: Project | null;
  onDirtyChange?: (dirty: boolean) => void;
}

const ProjectPricing: React.FC<ProjectPricingProps> = ({ scenarioId, setScenarioId, project, onDirtyChange }) => {
  const { t } = useTranslation(['common', 'screens']);
  const { scenarios } = useProjectScenarios();
  const scenario = scenarios.find(s => s.id === scenarioId) || scenarios[0];
  const plan = scenario?.ganttChartPlan;
  const estimateItems = scenario?.estimateItems || [];
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const provider = new ProjectsScenariosRestDataProvider();
  const exportProvider = new ExcelExportRestDataProvider();

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Helper to get phase name by id
  const phaseMap = useMemo(() =>
    plan?.phases ? Object.fromEntries(plan.phases.map(p => [p.id, p.name])) : {},
    [plan]
  );


  // --- CALCULATIONS (all at top) ---
  // Effort table: phase/resource breakdown
  let totalHours = 0;
  let totalCost = 0;
  // Calculate effort cost and commercial totals for margin calculation
  let totalEffortCost = 0;
  let totalEffortCommercial = 0;
  let totalEffortHours = 0;
  let contigencyTotal = 0;
  let margin = null;

  // Per-phase breakdowns for Effort table
  const phaseBreakdowns = (plan?.phases || []).map(phase => {
    let phaseHours = 0;
    let phaseCost = 0;
    let phaseCostSubtotal = 0;
    const resourceRows = (phase.resources || []).map(resource => {
      const allocatedWeeks = (resource.allocations || []).filter(a => a.allocated).length;
      const hours = (Number(resource.fte) || 0) * 40 * allocatedWeeks;
      const costRate = Number(resource.costRate) || 0;
      const commercialRate = Number(resource.commercialRate) || 0;
      const costSubtotal = costRate * hours;
      const subtotal = commercialRate * hours;
      // Margin % for this resource (per hour):
      let marginPercent = null;
      if (commercialRate > 0) {
        marginPercent = ((commercialRate - costRate) / commercialRate) * 100;
      }
      phaseHours += hours;
      phaseCost += subtotal;
      phaseCostSubtotal += costSubtotal;
      // For grand totals:
      totalEffortCost += costSubtotal;
      totalEffortCommercial += subtotal;
      totalEffortHours += hours;
      return { resource, hours, costSubtotal, subtotal, marginPercent };
    });
    totalHours += phaseHours;
    totalCost += phaseCost;
    return { phase, phaseHours, phaseCost, phaseCostSubtotal, resourceRows };
  });

  // Contingency (fixed price only)
  if (project?.isFixedPrice) {
    contigencyTotal = totalCost * ((project.fixedPriceContingencyPercentage ?? 0) / 100);
  }

  // Margin (for Pricing Health)
  // Only calculate if there is any commercial value
  if (totalEffortCommercial > 0) {
    margin = ((totalEffortCommercial - totalEffortCost) / totalEffortCommercial) * 100;
  } else {
    margin = null;
  }

  // Cost Groups/Items breakdown
  let costGroupsGrandTotal = 0;
  const costGroupsBreakdown = Array.isArray(scenario.costGroups)
    ? scenario.costGroups.map(group => {
        let groupSubtotal = 0;
        const items = Array.isArray(group.items) ? group.items.map(item => {
          const total = (Number(item.cost) || 0) * (Number(item.multiplier) || 0);
          groupSubtotal += total;
          return { ...item, total };
        }) : [];
        costGroupsGrandTotal += groupSubtotal;
        return { group, groupSubtotal, items };
      })
    : [];

  // Totals for panels
  const effortSubtotal = project?.isFixedPrice ? (totalCost + contigencyTotal) : totalCost;
  const costsSubtotal = costGroupsGrandTotal;
  const totalExVat = effortSubtotal + costsSubtotal;
  const vatPercentage = project?.vatPercentage ?? 21;
  const vatAmount = totalExVat * (vatPercentage / 100);
  const totalInclVat = totalExVat + vatAmount;

  if (!plan || !plan.phases) return <SubsectionPanel title={t('screens:projects.tabs.pricing')}><div>{t('screens:projectPricing.noPricingData')}</div></SubsectionPanel>;

  // Save handler for ScenarioSwitcher
  const handleSave = async () => {
    if (!scenario.id) return;
    setSaving(true);
    setIsDirty(false);
    try {
      await provider.update(scenario.id, scenario);
    } finally {
      setSaving(false);
    }
  };

  // Example: set isDirty to true on any user change
  const handleChange = () => {
    setIsDirty(true);
    // ...existing change logic...
  };



  // Excel download handler
  const handleDownloadExcel = async () => {
    try {
      if(!scenario?.id || !project?.id) {
        toast.error(t('screens:projectPricing.downloadError'));
        return;
      }
      const blob = await exportProvider.downloadPricing(scenario.id, project.id);
      if (blob) {
        const fileName = `Project_Pricing_${scenario.name || 'Scenario'}.xlsx`;
        saveAs(blob, fileName);
      } else {
        console.error('No data received for Excel download');
      }
    } catch (error) {
      toast.error(t('screens:projectPricing.downloadError'));
    }
  };

  return (
    <>
      <div className="pp-toolbar">
        <ScenarioSwitcher scenarioId={scenario?.id} setScenarioId={setScenarioId} onSave={handleSave} saving={saving} />
        <button className="pp-download-btn" onClick={handleDownloadExcel}>
          ⬇ {t('screens:projectPricing.downloadExcel')}
        </button>
      </div>

      {/* Pricing Overview */}
      <SubsectionPanel title={t('screens:projectPricing.pricingOverview')}>
        <div className="pp-metrics">
          {/* Effort Group */}
          <div className="pp-metrics__group">
            <div className="pp-metrics__group-label">{t('screens:projectPricing.effort')}</div>
            <div className="pp-metrics__items">
              <div className="pp-metrics__item">
                <div className="pp-metrics__item-label">{t('screens:projectPricing.totalCost')}</div>
                <div className="pp-metrics__value">{totalEffortCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
              <div className="pp-metrics__item">
                <div className="pp-metrics__item-label">{t('screens:projectPricing.totalComm')}</div>
                <div className="pp-metrics__value">{totalEffortCommercial.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
              <div className="pp-metrics__item">
                <div className="pp-metrics__item-label">{t('screens:projectPricing.margin')}</div>
                <div className="pp-metrics__value">{margin !== null ? margin.toLocaleString(undefined, { maximumFractionDigits: 1 }) + '%' : '–'}</div>
              </div>
            </div>
          </div>

          {/* Contingency Group */}
          <div className="pp-metrics__group">
            <div className="pp-metrics__group-label">{t('screens:projectPricing.contingencyOnEffort')}</div>
            <div className="pp-metrics__items">
              <div className="pp-metrics__item">
                <div className="pp-metrics__item-label">{t('screens:projectPricing.applied')}</div>
                <div className="pp-metrics__value">{project?.isFixedPrice ? t('common:yes') : t('common:no')}</div>
              </div>
              <div className="pp-metrics__item">
                <div className="pp-metrics__item-label">%</div>
                <div className="pp-metrics__value">
                  {project?.isFixedPrice ? (project.fixedPriceContingencyPercentage ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 }) + '%' : '0%'}
                </div>
              </div>
              <div className="pp-metrics__item">
                <div className="pp-metrics__item-label">{t('screens:projectPricing.amount')}</div>
                <div className="pp-metrics__value">
                  {project?.isFixedPrice ? (contigencyTotal || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0.00'}
                </div>
              </div>
            </div>
          </div>

          {/* Costs Group */}
          <div className="pp-metrics__group">
            <div className="pp-metrics__group-label">{t('screens:projectPricing.costs')}</div>
            <div className="pp-metrics__items">
              <div className="pp-metrics__item">
                <div className="pp-metrics__item-label">{t('common:total')}</div>
                <div className="pp-metrics__value">
                  {Array.isArray(scenario.costGroups)
                    ? scenario.costGroups.reduce((sum, group) => sum + (Array.isArray(group.items)
                      ? group.items.reduce((gSum, item) => gSum + ((Number(item.cost) || 0) * (Number(item.multiplier) || 0)), 0)
                      : 0), 0).toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : '0.00'}
                </div>
              </div>
            </div>
          </div>

          {/* Grand Total Group */}
          <div className="pp-metrics__group">
            <div className="pp-metrics__group-label">{t('screens:projectPricing.grandTotal')}</div>
            <div className="pp-metrics__items">
              <div className="pp-metrics__item">
                <div className="pp-metrics__item-label">{t('screens:projectPricing.totalExVat')}</div>
                <div className="pp-metrics__value">{totalExVat.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
              <div className="pp-metrics__item">
                <div className="pp-metrics__item-label">{t('screens:projectPricing.totalIncVat')}</div>
                <div className="pp-metrics__value">{totalInclVat.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
            </div>
          </div>
        </div>
      </SubsectionPanel>

      {/* Effort Table */}
      <SubsectionPanel title={t('screens:projectPricing.effortPanel')}>
        <InfoPanel header="About the effort table">
          The subtotals are based on the commercial rates and hours allocated to each resource in the project. The contingency is applied only for fixed-price projects.
        </InfoPanel>
        <div className="pp-table-container">
          <table className="pp-table">
            <thead>
              <tr>
                <th>{t('screens:projectPricing.nameRole')}</th>
                <th className="pp-col--right">{t('screens:projectPricing.costsCol')}</th>
                <th className="pp-col--right">{t('screens:projectPricing.commercialRate')}</th>
                <th className="pp-col--right">{t('screens:projectPricing.marginPct')}</th>
                <th className="pp-col--right">{t('screens:projectPricing.hourAmount')}</th>
                <th className="pp-col--right">{t('screens:projectPricing.subtotalCosts')}</th>
                <th className="pp-col--right">{t('screens:projectPricing.subtotalCommercial')}</th>
              </tr>
            </thead>
            <tbody>
              {phaseBreakdowns.map(({ phase, phaseHours, phaseCost, phaseCostSubtotal, resourceRows }) => (
                <React.Fragment key={phase.id}>
                  <tr className="pp-subheader-row">
                    <td colSpan={7}>{phaseMap[phase.id] || phase.name}</td>
                  </tr>
                  {resourceRows.map(({ resource, hours, costSubtotal, subtotal, marginPercent }) => (
                    <tr className="pp-data-row" key={resource.id + '-detail'}>
                      <td className="pp-col--indent">{resource.name}</td>
                      <td className="pp-col--right">{resource.costRate != null ? Number(resource.costRate).toLocaleString(undefined, { maximumFractionDigits: 2 }) : ''}</td>
                      <td className="pp-col--right">{resource.commercialRate != null ? Number(resource.commercialRate).toLocaleString(undefined, { maximumFractionDigits: 2 }) : ''}</td>
                      <td className="pp-col--right">{marginPercent !== null ? marginPercent.toLocaleString(undefined, { maximumFractionDigits: 2 }) + '%' : '–'}</td>
                      <td className="pp-col--right">{hours}</td>
                      <td className="pp-col--right">{costSubtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      <td className="pp-col--right">{subtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  <tr className="pp-subtotal-row">
                    <td colSpan={4} className="pp-col--indent">{t('screens:projectPricing.subtotals')}</td>
                    <td className="pp-col--right">{phaseHours}</td>
                    <td className="pp-col--right">{phaseCostSubtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td className="pp-col--right">{phaseCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  </tr>
                </React.Fragment>
              ))}
              <tr className="pp-total-row">
                <td colSpan={4}>{t('screens:projectPricing.subtotal')}</td>
                <td className="pp-col--right">{totalHours}</td>
                <td className="pp-col--right">{totalEffortCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                <td className="pp-col--right">{totalCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
              </tr>
              {project?.isFixedPrice && (
                <tr className="pp-contingency-row">
                  <td colSpan={6}>
                    Including fixed price contingency (based on effort): {project.fixedPriceContingencyPercentage}% ({contigencyTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })})
                  </td>
                  <td className="pp-col--right">{(totalCost + contigencyTotal).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SubsectionPanel>

      {/* Costs Table */}
      <SubsectionPanel title={t('screens:projectPricing.costsPanel')}>
        <InfoPanel header="About the costs table">
          The costs table shows all cost groups and items defined in the project scenario. This can also be viewed as the bill of materials for the project.
        </InfoPanel>
        <div className="pp-table-container">
          <table className="pp-table">
            <thead>
              <tr>
                <th>{t('screens:projectPricing.costGroup')}</th>
                <th>{t('screens:projectPricing.itemName')}</th>
                <th className="pp-col--right">{t('screens:projectPricing.cost')}</th>
                <th className="pp-col--right">{t('screens:projectPricing.multiplier')}</th>
                <th className="pp-col--right">{t('common:total')}</th>
              </tr>
            </thead>
            <tbody>
              {costGroupsBreakdown.length > 0 ? (
                costGroupsBreakdown.map(({ group, groupSubtotal, items }, groupIdx) => (
                  <React.Fragment key={group.name + '-' + groupIdx}>
                    <tr className="pp-subheader-row">
                      <td colSpan={5}>{group.name}</td>
                    </tr>
                    {items.length > 0 ? (
                      items.map((item, itemIdx) => (
                        <tr className="pp-data-row" key={item.id || itemIdx}>
                          <td></td>
                          <td>{item.name}</td>
                          <td className="pp-col--right">{item.cost?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="pp-col--right">{item.multiplier}</td>
                          <td className="pp-col--right">{item.total.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="pp-data-row">
                        <td colSpan={5} className="pp-col--muted">{t('screens:projectPricing.noCostItems')}</td>
                      </tr>
                    )}
                    <tr className="pp-subtotal-row">
                      <td colSpan={4} className="pp-col--indent">{t('screens:projectPricing.subtotalForGroup')}</td>
                      <td className="pp-col--right">{groupSubtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    {groupIdx === costGroupsBreakdown.length - 1 && (
                      <tr className="pp-total-row">
                        <td colSpan={4}>{t('screens:projectPricing.totalCostsRow')}</td>
                        <td className="pp-col--right">{costsSubtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr className="pp-data-row">
                  <td colSpan={5} className="pp-col--muted">{t('screens:projectCosts.noCostGroups')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SubsectionPanel>

      {/* Totals Summary */}
      <SubsectionPanel title={t('screens:projectPricing.totalsPanel')}>
        <table className="pp-totals-table">
          <tbody>
            <tr className="pp-totals-row--label">
              <td>{t('screens:projectPricing.effortSubtotal')}</td>
              <td className="pp-col--right">
                {project?.isFixedPrice
                  ? (totalCost + contigencyTotal).toLocaleString(undefined, { maximumFractionDigits: 2 })
                  : totalCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </td>
            </tr>
            <tr className="pp-totals-row--label">
              <td>{t('screens:projectPricing.costsSubtotal')}</td>
              <td className="pp-col--right">{costsSubtotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
            </tr>
            <tr className="pp-totals-row--total">
              <td>{t('screens:projectPricing.totalExclVat')}</td>
              <td className="pp-col--right">{totalExVat.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
            </tr>
            <tr className="pp-totals-row--vat">
              <td>VAT ({project?.vatPercentage ?? 21}%)</td>
              <td className="pp-col--right">{vatAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
            </tr>
            <tr className="pp-totals-row--total">
              <td>{t('screens:projectPricing.totalInclVat')}</td>
              <td className="pp-col--right">{totalInclVat.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
            </tr>
          </tbody>
        </table>
      </SubsectionPanel>
    </>
  );
};

export default ProjectPricing;
